import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IoIosLink } from 'react-icons/io';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';
import { InlineInputCardWrapper } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { LinkInputPopover } from '@/features/editor/widgets/custom-widget/inputs/shared/link/link-popover';
import { useOnChange } from '@/components/hooks/use-on-change';

export interface HtmlAttrLinkInputProps {
  initialValue?: string;
  onChange?: (value: string) => void;
}

export function HtmlAttrLinkInput({
  initialValue,
  onChange,
}: HtmlAttrLinkInputProps) {
  const containerRef = useRef<HTMLElement>(null);
  const [value, setValue] = useState(initialValue || ``);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const linkValue = useMemo(() => {
    if (value === `#`) {
      return `Scroll to top (#)`;
    }
    return value;
  }, [value]);

  const getParentRect = useCallback(() => {
    if (containerRef) {
      return containerRef?.current?.getBoundingClientRect();
    }
    return {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: 0,
      width: 0,
    } as DOMRect;
  }, [containerRef]);

  const onLinkSelected = (link: string) => {
    setIsMenuOpen(false);
    setValue(link);
  };

  useOnChange(() => {
    onChange?.(value);
  }, [value]);

  return (
    <Wrapper ref={containerRef}>
      <span>Link To</span>
      <Popover
        isOpen={isMenuOpen}
        content={
          <LinkInputPopover
            defaultValue={linkValue}
            width={getParentRect()?.width}
            onClose={() => setIsMenuOpen(false)}
            onSelection={onLinkSelected}
          />
        }
        onClickOutside={() => setIsMenuOpen(false)}
        positions={[`bottom`]}
        contentLocation={getParentRect()}
        containerStyle={{ zIndex: `100`, top: `-285px` }}
      >
        <Value onClick={() => setIsMenuOpen(true)}>
          {linkValue || `Click to select a link`}
        </Value>
      </Popover>
      <IoIosLink size={18} />
    </Wrapper>
  );
}

const Wrapper = styled(InlineInputCardWrapper)`
  && {
    display: grid;
    grid-template-columns: auto 1fr auto;
    text-overflow: ellipsis;
    max-width: 100%;
    gap: 2rem;
    align-items: center;
    justify-content: center;
  }
`;

const Value = styled.span`
  width: 100%;
  display: block;
  max-width: 100%;
  min-width: 100%;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;
  font-weight: 500 !important;
  font-family: Inter, sans-serif;
  font-size: 1.45rem;
  margin-top: -0.1rem;
  color: rgb(75, 85, 100);
  cursor: pointer;
  text-align: end;

  :hover {
    color: black;
  }

  transition: color 0.2s ease-in-out;
`;
